import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCheckCircle } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { clientesService } from '../../services';

// Formatea una fecha 'YYYY-MM-DD' como local (evita el desfase de un día por UTC)
const formatFecha = (dateStr) => {
  if (!dateStr) return 'N/A';
  const parts = String(dateStr).split('T')[0].split('-');
  if (parts.length === 3) {
    return new Date(+parts[0], +parts[1] - 1, +parts[2]).toLocaleDateString('es-CO');
  }
  return new Date(dateStr).toLocaleDateString('es-CO');
};

// Traduce el método de pago (enum del backend) a una etiqueta en español
const METODO_PAGO_LABEL = {
  cash: 'Efectivo',
  bank_transfer: 'Transferencia',
  credit_card: 'Tarjeta de crédito',
  debit_card: 'Tarjeta de débito',
  check: 'Cheque',
  electronic: 'Electrónico',
  other: 'Otro'
};

const ClientePagos = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pagos, setPagos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPagos();
  }, [id]);

  const loadPagos = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Obtener pagos del cliente desde la API
      const result = await clientesService.getPagosCliente(id, {
        skip: 0,
        limit: 20
      });

      if (result.success) {
        // Manejar diferentes estructuras de respuesta
        const dataArray = Array.isArray(result.data)
          ? result.data
          : (result.data?.items || result.data?.payments || []);

        const pagosData = dataArray.map(p => ({
          id: p.id,
          fecha: p.payment_date || p.created_at,
          facturaId: p.invoice_number || `INV-${p.invoice_id}`,
          invoiceId: p.invoice_id,
          monto: p.amount || 0,
          metodoPago: METODO_PAGO_LABEL[p.payment_method] || p.payment_method || 'N/A',
          referencia: p.reference_number || 'N/A',
          confirmado: p.is_confirmed,
          notas: p.notes || ''
        }));

        setPagos(pagosData);
      } else {
        setError(result.message || 'Error al cargar los pagos');
      }
    } catch (error) {
      console.error('Error cargando pagos:', error);
      setError('Error de conexión al cargar los pagos');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingGrid count={3} message="Cargando historial de pagos..." />;
  }

  const totalPagado = pagos.reduce((sum, p) => sum + p.monto, 0);

  return (
    <div className="cliente-pagos-page">
      {/* Header */}
      <div className="page-header-simple">
        <button onClick={() => navigate(`/system/clientes/${id}`)} className="btn btn--ghost">
          <FiArrowLeft /> Volver
        </button>
        <h1 className="page-title">PAGOS HECHOS</h1>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Resumen */}
      <div className="pagos-summary">
        <div className="summary-card">
          <div className="summary-label">Total Pagado</div>
          <div className="summary-value">${totalPagado.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Transacciones</div>
          <div className="summary-value">{pagos.length}</div>
        </div>
      </div>

      {/* Lista de pagos */}
      {pagos.length === 0 ? (
        <div className="empty-state">
          <p>No hay pagos registrados para este cliente</p>
        </div>
      ) : (
        <div className="pagos-list">
          {pagos.map((pago) => (
          <div key={pago.id} className="pago-card">
            <div className="pago-header">
              <div className="pago-fecha">
                <FiCheckCircle className="check-icon" />
                {formatFecha(pago.fecha)}
              </div>
              <span
                className="pago-tipo"
                style={{
                  padding: '2px 10px',
                  borderRadius: '10px',
                  fontSize: '0.75em',
                  fontWeight: 600,
                  backgroundColor: pago.confirmado ? '#d1fae5' : '#fef3c7',
                  color: pago.confirmado ? '#065f46' : '#92400e'
                }}
              >
                {pago.confirmado ? 'Confirmado' : 'Pendiente'}
              </span>
            </div>

            <div className="pago-body">
              <div className="pago-factura">
                <span className="label">Factura:</span>
                <span className="value">{pago.facturaId}</span>
              </div>

              <div className="pago-metodo">
                <span className="label">Método:</span>
                <span className="value">{pago.metodoPago}</span>
              </div>

              <div className="pago-referencia">
                <span className="label">Referencia:</span>
                <span className="value">{pago.referencia}</span>
              </div>
            </div>

            <div className="pago-footer">
              <div className="pago-monto">
                <span className="label">Monto:</span>
                <span className="monto">${pago.monto.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Menú inferior */}
      <BottomTabMenu baseRoute={`/system/clientes/${id}`} currentTab="pagos" />
    </div>
  );
};

export default ClientePagos;
